import { endOfDay, formatISO, startOfDay, subDays } from "date-fns";
import { TrendingDown, TrendingUp } from "lucide-react";
import { AnalyticsIntervalAlias } from "../types";
import crypto from "node:crypto";
import { usZip5Base } from "../usZip";

export function capitalizeInitials(storeName: string): string {
  // Split store name into words
  const words = storeName.trim().split(/\s+/);

  // Extract initials based on word count
  if (words.length === 1) {
    return words[0][0].toUpperCase();
  } else {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
}

export const formatPrice = (amount: number) => {
  return amount?.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Selling price: explicit markdown (including 0) wins over base price when set. */
export function sellingPrice(
  price: number | null | undefined,
  markdown: number | null | undefined,
): number | null {
  if (markdown != null) return markdown;
  if (price != null) return price;
  return null;
}

/** Real period-over-period %; `null` when `prev === 0` and `current > 0` (no meaningful %). */
export function calcPercentChange(
  current: number,
  prev: number,
): number | null {
  if (prev === 0) return current === 0 ? 0 : null;
  return parseFloat((((current - prev) / prev) * 100).toFixed(1));
}

/** Strip to digits (e.g. for `tel:` URIs and clipboard). */
export function digitsOnlyPhone(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "";
  return raw.replace(/\D/g, "");
}

export const formatUSPhoneNumber = (
  rawValue: string | null | undefined,
): string => {
  if (rawValue == null || rawValue === "") return "";
  const digitsOnly = rawValue.replace(/\D/g, "").slice(0, 10);
  if (digitsOnly.length === 0) return "";
  if (digitsOnly.length < 4) {
    return `(${digitsOnly}`;
  }
  if (digitsOnly.length < 7) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  }
  return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(
    3,
    6
  )}-${digitsOnly.slice(6, 10)}`;
};

// export function formatUSPhoneNumber(number: string): string {
//   if (!number?.startsWith("+1") || number.length !== 12) {
//     return number; // fallback to original if not a valid US E.164 number
//   }
//   const areaCode = number.slice(2, 5);
//   const prefix = number.slice(5, 8);
//   const lineNumber = number.slice(8);

//   return `(${areaCode}) ${prefix}-${lineNumber}`;
// }

export const formatChange = (value: number) => {
  if (value < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-red-500">
        {<TrendingDown className="size-3" />}
        {value.toFixed(0)}%
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-green-500">
      {<TrendingUp className="size-3" />}
      {"+" + value.toFixed(0)}%
    </span>
  );
};

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    // Possessives / contractions: remove apostrophes instead of turning them into hyphens
    // (e.g. "Eben's Store" -> "ebens-store", not "eben-s-store").
    .replace(/[''\u2018\u2019]/g, "")
    .replace(/[^a-z0-9]+/g, "-") // Replace remaining non-alphanumeric runs with a single dash
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing dashes
}

/**
 * True when `value` matches the shape of store URL slugs from {@link slugify}
 * (lowercase letters, digits, hyphens only). Using this before `ilike` on slug
 * avoids `%` / `_` being interpreted as SQL LIKE wildcards.
 */
export function isPublicStoreSlugParam(value: string): boolean {
  return /^[a-z0-9-]{1,200}$/.test(value);
}

export const toE164US = (formattedOrRaw: string): string => {
  const digits = formattedOrRaw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return `+${digits}`;
};

export function display_alias(alias: AnalyticsIntervalAlias) {
  if (alias.endsWith(" DAY")) {
    return "Last " + alias.toLowerCase() + "s";
  }

  if (alias === AnalyticsIntervalAlias.CUSTOM) {
    return "custom range";
  }

  if (alias === AnalyticsIntervalAlias.LAST_YEAR) {
    return "Last Year";
  }

  return alias.charAt(0).toUpperCase() + alias.slice(1).toLowerCase();
}

export function getDefaultAnalyticsInterval(): {
  start: string;
  end: string;
  alias: AnalyticsIntervalAlias;
} {
  const endDate = new Date();
  const startDate = subDays(endDate, 29);
  const defaultInterval = AnalyticsIntervalAlias.LAST_30_DAYS;
  return {
    start: formatISO(startOfDay(startDate)),
    end: formatISO(endOfDay(endDate)),
    alias: defaultInterval,
  };
}

export function makeOrderNumber() {
  // 10 bytes -> ~16 chars base32-ish after formatting
  const buf = crypto.randomBytes(10);
  const raw = buf.toString("base64url").toUpperCase(); // random-looking
  const compact = raw.replace(/[^A-Z0-9]/g, "").slice(0, 12);
  return `BBP-${compact.slice(0, 4)}-${compact.slice(4, 8)}-${compact.slice(
    8,
    12
  )}`;
}

export const formatPaymentMethod = (paymentType: string) => {
  switch (paymentType) {
    case "PAYPAL":
      return "PayPal";
    case "CREDIT":
      return "Credit Card";
    case "CASH":
      return "Cash";
    case "ZELLE":
      return "Zelle";
    case "CHECK":
      return "Cheque";
    default:
      return "Cash";
  }
};

export function getDominantColor(
  imageElement: HTMLImageElement
): Promise<string> {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) return resolve("#ffffff");

    canvas.width = imageElement.naturalWidth;
    canvas.height = imageElement.naturalHeight;

    ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const colorMap: Record<string, number> = {};
    let maxColor = "";
    let maxCount = 0;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const alpha = data[i + 3];

      if (alpha < 128) continue; // skip transparent pixels

      const key = `${r},${g},${b}`;

      colorMap[key] = (colorMap[key] || 0) + 1;

      if (colorMap[key] > maxCount) {
        maxCount = colorMap[key];
        maxColor = key;
      }
    }

    const [r, g, b] = maxColor.split(",").map(Number);
    const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b)
      .toString(16)
      .slice(1)}`;

    resolve(hex);
  });
}

/**
 * Calculates the distance in miles between two geographic coordinates
 * using the Haversine formula.
 */
export function calculateHaversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) *
    Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Converts a US zip code to latitude and longitude coordinates.
 * ZIP+4 and other formats are normalized to ZIP5 for Zippopotam (ZIP+4 paths 404).
 */
export async function getCoordinatesFromZipCode(
  zipCode: string | number | null | undefined
): Promise<{ lat: number; lng: number } | null> {
  const baseZip = usZip5Base(zipCode);
  if (!baseZip) return null;

  try {
    const response = await fetch(`https://api.zippopotam.us/us/${baseZip}`);

    if (!response.ok) {
      console.warn(
        `Failed to get coordinates for zip code ${baseZip}:`,
        response.statusText
      );
      return null;
    }

    const data = await response.json();
    if (data.places && data.places.length > 0) {
      const place = data.places[0];
      return {
        lat: parseFloat(place.latitude),
        lng: parseFloat(place.longitude),
      };
    }
    return null;
  } catch (error) {
    console.warn(`Error fetching coordinates for zip code ${baseZip}:`, error);
    return null;
  }
}
